import { ContextPanelHeader } from './context-panel-header';
import { ContextPanelOutlet } from './context-panel-outlet';
import { ContextPanelProvider } from './context-panel-provider';
import { ContextPanelRegion } from './context-panel-region';
import { ContextPanelSlot } from './context-panel-slot';

export type {
  ContextPanelContextValue,
  ContextPanelOpenOptions,
  ContextPanelScope,
  ContextPanelWithDefaults,
} from './context-panel-context';
export { useContextPanel } from './context-panel-context';
export type { ContextPanelHeaderProps } from './context-panel-header';
export type { ContextPanelOutletProps } from './context-panel-outlet';

export interface ContextPanelNamespace {
  /**
   * A convenience header for consistent panel header styling with an optional
   * close button and tinted background.
   */
  Header: typeof ContextPanelHeader;
  /**
   * The presentational panel element that renders where content appears.
   * Used internally by scope integrations (Page, Layout, Region).
   */
  Outlet: typeof ContextPanelOutlet;
  /**
   * State management provider for a given scope. Pre-placed in Layout
   * (global) and Page (page), or manually wrapped for component scope.
   */
  Provider: typeof ContextPanelProvider;
  /**
   * A layout wrapper for ad-hoc component-scope panels. Provides the
   * provider and renders a flex-row section with the panel alongside content.
   */
  Region: typeof ContextPanelRegion;
  /**
   * A declarative component that opens the panel when mounted and closes it
   * when unmounted. Useful for persistent panels or when content is known at
   * render time.
   */
  Slot: typeof ContextPanelSlot;
}

/**
 * Namespaced export for compound component usage:
 *
 * @example
 * <ContextPanel.Region>
 *   <Content />
 * </ContextPanel.Region>
 *
 * <ContextPanel.Header onClose={close}>
 *   Title
 * </ContextPanel.Header>
 *
 * <ContextPanel.Slot scope="page" header={...} persistent>
 *   <ActivityFeed />
 * </ContextPanel.Slot>
 */
export const ContextPanel: ContextPanelNamespace = {
  Header: ContextPanelHeader,
  Outlet: ContextPanelOutlet,
  Provider: ContextPanelProvider,
  Region: ContextPanelRegion,
  Slot: ContextPanelSlot,
};

export default ContextPanel;
